import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import axios from "axios";
import { toast } from "sonner";
import { Mail, ArrowLeft, CheckCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetToken, setResetToken] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      // For demo purposes, if token is returned (no email configured), show it
      if (response.data.token) {
        setResetToken(response.data.token);
      }
      toast.success("Reset link sent! Check your email.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center shadow-lg">
              <span className="text-[#020617] font-bold text-xl tracking-tight">A+</span>
            </div>
            <span className="font-['Playfair_Display'] text-3xl text-white">Advanta <span className="text-[#D4AF37]">Plus</span></span>
          </Link>
        </div>

        <div className="bg-[#0B0F19] rounded-2xl p-8 border border-[#D4AF37]/10">
          {!sent ? (
            <>
              <div className="text-center mb-6">
                <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-7 h-7 text-[#D4AF37]" />
                </div>
                <h1 className="font-['Playfair_Display'] text-2xl text-white mb-2">
                  Forgot Password?
                </h1>
                <p className="text-white/60 text-sm">
                  Enter your email and we'll send you a reset link
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-white/70">Email Address</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    data-testid="forgot-email-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="btn-gold w-full py-6 text-base rounded-xl"
                  disabled={loading}
                  data-testid="forgot-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    "Send Reset Link"
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="font-['Playfair_Display'] text-2xl text-white mb-2">
                Check Your Email
              </h2>
              <p className="text-white/60 text-sm mb-6">
                We've sent a password reset link to<br/>
                <span className="text-white">{email}</span>
              </p>
              
              {resetToken && (
                <div className="bg-[#D4AF37]/10 rounded-lg p-4 mb-6 text-left">
                  <p className="text-[#D4AF37] text-xs font-medium mb-2">Demo Mode - Reset Link:</p>
                  <Link 
                    to={`/reset-password?token=${resetToken}`}
                    className="text-white text-sm break-all hover:text-[#D4AF37]"
                  >
                    Click here to reset password
                  </Link>
                </div>
              )}

              <Button 
                onClick={() => { setSent(false); setEmail(""); setResetToken(null); }}
                className="btn-gold-outline px-6 py-3 rounded-xl"
              >
                Try Different Email
              </Button>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#F5DEB3] text-sm">
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
